import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconChevronDown } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import type { PracticeRailCorpus } from "../../domains/practice/hooks/usePracticeRailTerms";

/**
 * The practice, as a header dropdown.
 *
 * Bare text and a chevron rather than a pill, and that is the whole point of
 * moving it here: the practice is the CORPUS being read, not a filter that was
 * added to it, so it must not read as a peer of the chips below. A pill here
 * would put the corpus and its filters in the same visual class, which is the
 * confusion the practice rail had.
 *
 * It takes the place of the page title. There is no separate heading, by design:
 * the practice name was said twice, ten pixels apart, and this is the copy that
 * survived.
 *
 * It also holds "All practices", the one corpus here that is not a profile. It
 * belongs in this button and nowhere else: a corpus is chosen where corpora are
 * chosen, and putting the aggregate back on the rail as a chip is what the first
 * one did.
 *
 * ⚠️ Deliberately SMALLER than the title it replaces, and still not big enough for
 * the strings it holds. `Header`'s own `<h1>` is `text-2xl`, which is right for
 * "Discovery" and wrong for a 40-character median. Measured on a 375px viewport
 * against the real header (search + language + avatar on the right leave this
 * button 159px), on "Video-assisted and robotic thoracic surgery", 43 characters:
 *
 *   text-2xl (24px)        -> 11 characters before the ellipsis
 *   text-heading-xs (17px) -> 17
 *
 * So the smaller token buys half again as much name, and the name still does not
 * fit. That is the state of it: the two remaining ways out are dropping a header
 * icon and giving the dropdown its own line, and the second recreates the page
 * title this design removed. Neither is taken.
 *
 * `max-w-full` is what makes the overflow an ellipsis rather than a collision — a
 * flex button sizes to fit-content, so without it a long name walks over the
 * avatar.
 */
function PracticeDropdown({
  practices,
  corpus,
  onSelectCorpus,
}: {
  practices: ReadonlyArray<{ uid: string; name: string }>;
  /** `null` while the catalogue has not landed: the button renders no name. */
  corpus: PracticeRailCorpus | null;
  onSelectCorpus: (corpus: PracticeRailCorpus) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  // Menu disclosure, which is this button's own machinery and nothing the page
  // has an opinion about.
  const [menuOpen, setMenuOpen] = useState(false);
  const showsAll = corpus !== null && corpus.kind === "all";
  const selectedUid = corpus === null || showsAll ? null : corpus.uid;
  const current = practices.find((practice) => practice.uid === selectedUid);
  // 14 characters, so the aggregate is the one label this button never has to
  // truncate. Every practice name here is longer.
  const label = showsAll
    ? t("All practices")
    : current === undefined
      ? ""
      : current.name;

  return (
    <div className="relative min-w-0">
      <button
        type="button"
        aria-expanded={menuOpen}
        onClick={() => {
          setMenuOpen(!menuOpen);
        }}
        className="flex max-w-full min-w-0 cursor-pointer items-center gap-1"
      >
        <span className="min-w-0 truncate text-heading-xs font-semibold text-primary-black">
          {label}
        </span>
        <IconChevronDown size={16} stroke={2} className="shrink-0" />
      </button>
      {menuOpen && (
        <>
          {/* Tapping anywhere else closes it. A menu with no dismissal outside
              its own items is a trap on a touch screen, where there is no Escape
              key and no blur to lean on. */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setMenuOpen(false);
            }}
          />
          <div className="absolute top-full left-0 z-20 mt-2 flex w-64 flex-col overflow-hidden rounded-2xl border border-ds-slate-100 bg-white py-1 shadow-lg">
            {/* Long catalogues (a species-heavy specialty, say) can outgrow the
                viewport: this inner scroll region is what makes the rows below
                the fold reachable, following the same
                `min-h-0 max-h-[…] flex-1 overflow-y-auto` shape as `Select.tsx`.
                `dvh` rather than a fixed pixel cap, so the menu uses the actual
                device height instead of running short on a tall phone or long
                on a small one. The outer card stays `overflow-hidden` for the
                rounded corners. */}
            <div className="min-h-0 max-h-[70dvh] flex-1 overflow-y-auto overscroll-contain">
              {/* Offered ONLY to a reader holding more than one, because with a
                  single practice it is that practice under a second name: two rows,
                  one corpus, and a reader left wondering what the difference is. It
                  sits above a divider because it is not a peer of the rows below,
                  it is all of them. */}
              {practices.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onSelectCorpus({ kind: "all" });
                    }}
                    className={cn(
                      "w-full cursor-pointer px-4 py-2 text-left text-ui-body",
                      showsAll
                        ? "font-semibold text-ds-primary"
                        : "text-ds-black"
                    )}
                  >
                    {t("All practices")}
                  </button>
                  <div className="mx-4 my-1 border-t border-ds-slate-100" />
                </>
              )}
              {practices.map((practice) => (
                <button
                  key={practice.uid}
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onSelectCorpus({ kind: "practice", uid: practice.uid });
                  }}
                  className={cn(
                    "w-full cursor-pointer px-4 py-2 text-left text-ui-body",
                    practice.uid === selectedUid
                      ? "font-semibold text-ds-primary"
                      : "text-ds-black"
                  )}
                >
                  {practice.name}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export { PracticeDropdown };
